import { isEqual } from 'lodash';

import { StaleDocumentWriteError } from './rxdb-base';

/**
 * Wraps one write to a note or block. Autosaves guard with the `updatedAt` stamp of the document as
 * rendered, and the write layer rejects them if storage holds a different stamp. Right after this
 * app writes the document itself, the rendered stamp is briefly behind the stored one, so a guarded
 * save would be rejected for a conflict that is not one. This wrapper remembers which stamps our own
 * writes replaced and swaps in our latest one while the rendered document still shows an old one.
 * Any other stamp is a foreign write and is guarded against as is. Once the rendered document shows
 * our latest stamp the record is dropped.
 *
 * Bookkeeping is keyed by document id in a module map, so every writer of a document (editor host,
 * chat agent) shares one record. Stamps come from each writer's own clock, so only identity is
 * safe; they are never ordered.
 *
 * A newer stored revision with the same content and pending review is adopted instead of guarded
 * against: it only touched other fields, so a content write on top of it loses nothing. A revision
 * that attached or resolved a review changes what a content write means and stays guarded against.
 *
 * Unguarded writes go through here too, since they also bump `updatedAt`. A stamp is recorded as
 * replaced only when it was the stored stamp at write time; a stamp a foreign revision had already
 * replaced is never claimed. The read-to-patch window of an unguarded write is an accepted gap.
 *
 * A stale rejection forgets the record only when the write guarded with our own recorded stamp:
 * that disproves the record, and keeping it would reject every later save. A rejection of the
 * rendered stamp says nothing about a record another writer made meanwhile, so it is kept.
 */
export const guardOwnWrite = async <T extends { updatedAt?: string }>(
  renderedDocument: GuardedDocument | null | undefined,
  write: (ifUnmodifiedSince: string | undefined) => Promise<T>
): Promise<T> => {
  const docVisibleUpdatedAt = renderedDocument?.updatedAt;
  const storedUpdatedAt = renderedDocument?.getLatest().updatedAt;

  let ifUnmodifiedSince = docVisibleUpdatedAt;
  if (renderedDocument && docVisibleUpdatedAt !== undefined) {
    const latestDocument = renderedDocument.getLatest();
    const guardState = ownWriteGuardStates.get(renderedDocument.id);

    if (latestDocument.updatedAt !== docVisibleUpdatedAt && isSameEditableRevision(latestDocument, renderedDocument)) {
      ifUnmodifiedSince = latestDocument.updatedAt;
    } else if (docVisibleUpdatedAt === guardState?.latestOwnSavedUpdatedAt) {
      ownWriteGuardStates.delete(renderedDocument.id);
    } else if (guardState?.supersededUpdatedAt.has(docVisibleUpdatedAt)) {
      ifUnmodifiedSince = guardState.latestOwnSavedUpdatedAt;
    }
  }

  let saved: T;
  try {
    saved = await write(ifUnmodifiedSince);
  } catch (error) {
    // Re-read the record: another writer may have saved this document while ours was in flight.
    if (
      error instanceof StaleDocumentWriteError &&
      renderedDocument &&
      ownWriteGuardStates.get(renderedDocument.id)?.latestOwnSavedUpdatedAt === ifUnmodifiedSince
    ) {
      ownWriteGuardStates.delete(renderedDocument.id);
    }
    throw error;
  }

  const savedUpdatedAt = saved?.updatedAt;
  if (renderedDocument && savedUpdatedAt !== undefined) {
    const supersededUpdatedAt = ownWriteGuardStates.get(renderedDocument.id)?.supersededUpdatedAt ?? new Set<string>();
    if (ifUnmodifiedSince !== undefined && ifUnmodifiedSince !== savedUpdatedAt && ifUnmodifiedSince === storedUpdatedAt) {
      supersededUpdatedAt.add(ifUnmodifiedSince);
    }
    ownWriteGuardStates.set(renderedDocument.id, { supersededUpdatedAt, latestOwnSavedUpdatedAt: savedUpdatedAt });
  }

  return saved;
};

/** The document as the host rendered it; `getLatest` reads the revision RxDB holds right now. */
interface GuardedDocument extends GuardedRevision {
  getLatest: () => GuardedRevision;
}

interface GuardedRevision {
  id: string;
  updatedAt?: string;
  content?: string;
  pendingReview?: unknown;
}

/** An entry exists only while a save of that document is recorded, so it always has a latest stamp. */
interface OwnWriteGuardState {
  supersededUpdatedAt: Set<string>;
  latestOwnSavedUpdatedAt: string;
}

const ownWriteGuardStates = new Map<string, OwnWriteGuardState>();

// Writes sanitize `undefined` to `null`, so a review that was never set compares equal to one that was cleared.
const isSameEditableRevision = (a: GuardedRevision, b: GuardedRevision) =>
  a.content === b.content && isEqual(a.pendingReview ?? null, b.pendingReview ?? null);
